import type { AggregatedStudentReport } from '../../types/studentSummary'
import { formatNumber } from '../../utils/formatNumber'

interface AggregatedReportCardProps {
  report: AggregatedStudentReport | null
  loading: boolean
  averageVideoCount: number
  averageConversationCount: number
  groupName?: string | null
}

const AggregatedReportCard = ({
  report,
  loading,
  averageVideoCount,
  averageConversationCount,
  groupName
}: AggregatedReportCardProps) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    )
  }

  if (!report) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-gray-500">
          <p className="text-sm">AIによる全体レポートはまだ生成されていません</p>
          <p className="text-xs mt-1">翌日の処理後に表示されます</p>
        </div>
      </div>
    )
  }

  // Calculate weather percentages
  const total = report.sunny_count + report.cloudy_count + report.rainy_count
  const sunnyPercentage = total > 0 ? Math.round((report.sunny_count / total) * 100) : 0
  const cloudyPercentage = total > 0 ? Math.round((report.cloudy_count / total) * 100) : 0
  const rainyPercentage = total > 0 ? Math.round((report.rainy_count / total) * 100) : 0

  // Format the report period (from reportDate - lookback_days to reportDate)
  const endDate = new Date(report.report_date)
  const startDate = new Date(report.report_date)
  startDate.setDate(startDate.getDate() - report.lookback_days)

  const formatDate = (date: Date) => date.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  const reportPeriod = `${formatDate(startDate)}〜${formatDate(endDate)}`

  // Get aggregation level label
  const levelLabel = report.aggregation_level === 'school'
    ? '学校全体'
    : report.aggregation_level === 'grade'
    ? `${report.grade}`
    : report.aggregation_level === 'group'
    ? `グループ: ${groupName || '不明'}`
    : `${report.grade}${report.class}`

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">全体傾向（AI分析）</h3>
        <div className="text-xs text-gray-500">
          <span>{reportPeriod}</span>
          <span className="ml-2">・{levelLabel}</span>
        </div>
      </div>

      {/* Weather Distribution */}
      <div className="mb-6">
        <div className="flex items-center gap-6 mb-2">
          <div className="flex items-center gap-2">
            <span className="text-2xl" style={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif' }}>☀️</span>
            <span className="text-sm font-medium text-gray-700">
              {formatNumber(report.sunny_count)}名
            </span>
            <span className="text-sm text-gray-500">
              ({sunnyPercentage}%)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl" style={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif' }}>☁️</span>
            <span className="text-sm font-medium text-gray-700">
              {formatNumber(report.cloudy_count)}名
            </span>
            <span className="text-sm text-gray-500">
              ({cloudyPercentage}%)
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl" style={{ fontFamily: '"Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif' }}>☔</span>
            <span className="text-sm font-medium text-gray-700">
              {formatNumber(report.rainy_count)}名
            </span>
            <span className="text-sm text-gray-500">
              ({rainyPercentage}%)
            </span>
          </div>
        </div>

        <div className="text-sm text-gray-600">
          <span className="font-medium">対象人数:</span> {formatNumber(report.student_count)}名
        </div>
      </div>

      {/* Average Activity Stats */}
      <div className="flex items-center justify-center gap-2 text-sm text-gray-700 py-2 bg-gray-50 rounded mb-4">
        <span className="font-medium">平均動画数</span>
        <span className="text-blue-600 font-semibold">{averageVideoCount.toFixed(1)}本</span>
        <span className="text-gray-400">/</span>
        <span className="font-medium">平均ワーク数</span>
        <span className="text-green-600 font-semibold">{averageConversationCount.toFixed(1)}本</span>
      </div>

      {/* Summary Sections */}
      <div className="space-y-4">
        {/* Overall Trend */}
        {report.overall_trend && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-bold text-gray-800 mb-2">要約</h4>
            <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
              {report.overall_trend}
            </div>
          </div>
        )}

        {/* Common Strengths */}
        {report.common_strengths.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-bold text-gray-800 mb-2">良い点</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              {report.common_strengths.map((strength, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{strength}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Common Challenges */}
        {report.common_challenges.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-bold text-gray-800 mb-2">課題</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              {report.common_challenges.map((challenge, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{challenge}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Teacher Support Strategies */}
        {report.teacher_support_strategy.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-bold text-gray-800 mb-2">支援方針</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              {report.teacher_support_strategy.map((strategy, index) => (
                <li key={index} className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>{strategy}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export default AggregatedReportCard
